import React, { useState } from 'react';
import { Box, Skeleton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { formatHours } from '../monthly/format';
import { cardSx, cardTitleSx, INK, MUTED, SERIES } from './homeTheme';

const TOP = 5;

function topWithOther(items) {
  if (items.length <= TOP + 1) return items;
  const rest = items.slice(TOP).reduce((sum, i) => sum + i.minutes, 0);
  return [...items.slice(0, TOP), { name: `Other (${items.length - TOP})`, minutes: rest }];
}

/** Dove è andato il tempo del mese: ciambella per progetto o attività, con legenda. */
export default function TimeBreakdown({ stats }) {
  const [mode, setMode] = useState('projects');
  const source = mode === 'projects' ? stats.projects || [] : stats.activities || [];
  const items = topWithOther(source);
  const total = items.reduce((sum, i) => sum + i.minutes, 0);

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Typography sx={cardTitleSx}>Where your time went</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, v) => v && setMode(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', py: 0.25, px: 1.25, fontSize: 12.5 } }}
        >
          <ToggleButton value="projects">Projects</ToggleButton>
          <ToggleButton value="activities">Activities</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {!stats.ready ? (
        <Skeleton variant="rounded" height={200} />
      ) : !total ? (
        <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ color: MUTED, fontSize: 14 }}>No hours tracked this month yet.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ position: 'relative', width: 180, height: 180, flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={items} dataKey="minutes" nameKey="name" innerRadius={58} outerRadius={86} paddingAngle={2} stroke="none" isAnimationActive>
                  {items.map((i, idx) => (
                    <Cell key={i.name} fill={SERIES[idx % SERIES.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK, lineHeight: 1 }}>{formatHours(total)}h</Typography>
              <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.5 }}>{mode === 'projects' ? `${source.length} projects` : `${source.length} activities`}</Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map((i, idx) => {
              const share = Math.round((i.minutes / total) * 100);
              return (
                <Box key={i.name}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SERIES[idx % SERIES.length], flexShrink: 0 }} />
                    <Tooltip title={i.name} placement="top-start" enterDelay={600}>
                      <Typography noWrap sx={{ fontSize: 13, color: INK, flex: 1, minWidth: 0 }}>
                        {i.name}
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK }}>{formatHours(i.minutes)}h</Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED, width: 36, textAlign: 'right' }}>{share}%</Typography>
                  </Box>
                  <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'var(--rt-track)', mt: 0.5, ml: 2.25 }}>
                    <Box sx={{ height: '100%', width: `${share}%`, borderRadius: 2, bgcolor: SERIES[idx % SERIES.length], transition: 'width 0.5s ease' }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}
